/**
 * Browser sign-in against the Cognito pool, over SRP.
 *
 * The library keeps its tokens in localStorage and refreshes them itself, so
 * a reload does not need a password. `ADMIN_USER_PASSWORD_AUTH` is a shell
 * convenience and is not used here — see docs/architecture.md D6.
 *
 * An invited account arrives in `FORCE_CHANGE_PASSWORD`, so `signIn` may
 * resolve a `NewPasswordChallenge` instead of a session; `SignIn.tsx` collects
 * the new password and calls `complete`.
 *
 * A forgotten password is the person's own to fix: `requestPasswordReset`
 * emails a code and `ResetChallenge.confirm` spends it. Neither needs the API
 * or an admin — see docs/architecture.md D6.
 *
 * Somebody with two-step sign-in set up (#92) gets a `SecondStepChallenge`
 * after their password: a code from their authenticator app.
 */

import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
} from "amazon-cognito-identity-js";
import type { MfaMethod } from "@baros/core";
import { challengeAttributes, isUserNotFound, writableOnly } from "./password.js";

const pool = new CognitoUserPool({
  UserPoolId: import.meta.env.VITE_USER_POOL_ID,
  ClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
});

export interface Session {
  readonly email: string;
  readonly accessToken: string;
}

const EMAIL_KEY = "baros.email";

function sessionFrom(user: CognitoUser, s: CognitoUserSession): Session {
  return {
    email: localStorage.getItem(EMAIL_KEY) ?? user.getUsername(),
    accessToken: s.getAccessToken().getJwtToken(),
  };
}

/** The session from a previous sign-in, refreshed if needed, or null. */
export function currentSession(): Promise<Session | null> {
  return new Promise((resolve) => {
    const user = pool.getCurrentUser();
    if (!user) return resolve(null);
    user.getSession((err: Error | null, s: CognitoUserSession | null) => {
      if (err || !s || !s.isValid()) return resolve(null);
      resolve(sessionFrom(user, s));
    });
  });
}

/**
 * An invited account signing in for the first time.
 *
 * `inviteToPool` creates the Cognito user with a temporary password and no
 * `MessageAction`, so the account lands in `FORCE_CHANGE_PASSWORD` and SRP
 * answers with `NEW_PASSWORD_REQUIRED` instead of a session. The challenge
 * must be finished on the same `CognitoUser` — it holds the challenge session
 * — so it is handed to the UI as a closure rather than as data.
 */
export interface NewPasswordChallenge {
  readonly email: string;
  /**
   * Attributes the pool insists on that the challenge gave us no value for.
   * Empty for this pool (email is the only required attribute and
   * `AdminCreateUser` sets it), but a pool with a required custom attribute
   * would list it, and the UI asks for those rather than failing.
   */
  readonly missingAttributes: readonly string[];
  /** Set the password and continue into a normal session. */
  readonly complete: (
    newPassword: string,
    extraAttributes?: Record<string, string>,
  ) => Promise<Session>;
}

/**
 * The second step of a sign-in (#92): the password was right, and now the
 * authenticator app's code. Like the invite challenge it lives on the
 * `CognitoUser` that raised it, so it is handed over as a closure. A wrong
 * code can be tried again on the same challenge; one that has gone stale
 * means starting over.
 */
export interface SecondStepChallenge {
  readonly method: MfaMethod;
  readonly complete: (code: string) => Promise<Session>;
}

export type SignInResult =
  | { readonly kind: "session"; readonly session: Session }
  | { readonly kind: "new-password"; readonly challenge: NewPasswordChallenge }
  | { readonly kind: "second-step"; readonly challenge: SecondStepChallenge };

function answerAppCode(user: CognitoUser, email: string, code: string): Promise<Session> {
  return new Promise((resolve, reject) => {
    user.sendMFACode(
      code,
      {
        onSuccess: (s) => {
          localStorage.setItem(EMAIL_KEY, email);
          resolve(sessionFrom(user, s));
        },
        onFailure: (err: unknown) => reject(asError(err)),
      },
      "SOFTWARE_TOKEN_MFA",
    );
  });
}

function asError(err: unknown): Error {
  return err instanceof Error ? err : Object.assign(new Error(String(err)), { cause: err });
}

export function signIn(email: string, password: string): Promise<SignInResult> {
  return new Promise((resolve, reject) => {
    // `new CognitoUser({ Username: "" })` is allowed — the library only
    // refuses null — and the empty name goes to Cognito, which answers
    // "Missing required parameter USERNAME". Nobody can act on that, so it
    // never leaves here. See `fieldValue` for how one got this far.
    if (!email) return reject(new Error("Enter the email you sign in with."));
    if (!password) return reject(new Error("Enter your password."));
    const user = new CognitoUser({ Username: email, Pool: pool });
    user.authenticateUser(
      new AuthenticationDetails({ Username: email, Password: password }),
      {
        onSuccess: (s) => {
          localStorage.setItem(EMAIL_KEY, email);
          resolve({ kind: "session", session: sessionFrom(user, s) });
        },
        onFailure: (err: unknown) => reject(asError(err)),
        totpRequired: () =>
          resolve({
            kind: "second-step",
            challenge: { method: "totp", complete: (code) => answerAppCode(user, email, code) },
          }),
        // SMS is not offered; an account set up for it by hand says so.
        mfaRequired: () => reject(new Error("This account is set up for text-message codes, which Bar OS does not offer. Ask an admin.")),
        newPasswordRequired: (userAttributes: unknown, requiredAttributes: unknown) => {
          const { attributes, missing } = challengeAttributes(userAttributes, requiredAttributes);
          resolve({
            kind: "new-password",
            challenge: {
              email,
              missingAttributes: missing,
              complete: (newPassword, extraAttributes) =>
                completeNewPassword(user, email, newPassword, {
                  ...attributes,
                  ...writableOnly(extraAttributes ?? {}),
                }),
            },
          });
        },
      },
    );
  });
}

function completeNewPassword(
  user: CognitoUser,
  email: string,
  newPassword: string,
  attributes: Record<string, string>,
): Promise<Session> {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(newPassword, attributes, {
      onSuccess: (s) => {
        localStorage.setItem(EMAIL_KEY, email);
        resolve(sessionFrom(user, s));
      },
      onFailure: (err: unknown) => reject(asError(err)),
      // An invited account has never had a second step to set up, but the
      // library calls these without checking and would throw a TypeError
      // rather than say anything useful.
      mfaRequired: () => reject(new Error("Sign in again with your new password to finish.")),
      totpRequired: () => reject(new Error("Sign in again with your new password to finish.")),
    });
  });
}

/**
 * A forgotten password, reset by the person who forgot it.
 *
 * `ForgotPassword` emails a code to the account's verified address and
 * `ConfirmForgotPassword` spends that code to set a new password. Every
 * account `inviteToPool` creates has `email_verified` set, so there is always
 * somewhere for the code to go.
 *
 * Like the invite challenge above this is handed to the UI as closures: the
 * code belongs to the `CognitoUser` that asked for it, and the pool client is
 * all either call needs — no API route, no admin.
 */
export interface ResetChallenge {
  readonly email: string;
  /**
   * Cognito's masked address for the code, `h***@a***.com`, or null when it
   * did not say. Worth showing to someone with a work address and a personal
   * one; null also covers the address we declined to confirm an account for.
   */
  readonly destination: string | null;
  /** Spend the code, set the password, and sign in with it. */
  readonly confirm: (code: string, newPassword: string) => Promise<Session>;
  /** Email another code. The newest code is the one that works. */
  readonly resend: () => Promise<ResetChallenge>;
}

function codeDestination(data: unknown): string | null {
  const details = (data as { CodeDeliveryDetails?: { Destination?: unknown } } | null)
    ?.CodeDeliveryDetails;
  return typeof details?.Destination === "string" && details.Destination !== ""
    ? details.Destination
    : null;
}

function resetChallenge(
  user: CognitoUser,
  email: string,
  destination: string | null,
): ResetChallenge {
  return {
    email,
    destination,
    confirm: (code, newPassword) => confirmPasswordReset(user, email, code, newPassword),
    resend: () => requestPasswordReset(email),
  };
}

/** Ask Cognito to email a reset code, and resolve the step that spends it. */
export function requestPasswordReset(email: string): Promise<ResetChallenge> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: pool });
    const sent = (data: unknown) => resolve(resetChallenge(user, email, codeDestination(data)));
    user.forgotPassword({
      // The library raises `inputVerificationCode` when it is defined and
      // `onSuccess` when it is not. Both are passed so a version that changes
      // its mind cannot leave this promise pending for ever.
      inputVerificationCode: sent,
      onSuccess: sent,
      onFailure: (err: unknown) => {
        // An address with no account must look like one that has. The pool
        // client sets `preventUserExistenceErrors`, so Cognito itself answers
        // this with a fabricated delivery rather than an exception. This is
        // the other half of belt and braces, and covers a client deployed
        // before that setting was.
        // The code step is entered either way and no code ever matches.
        if (isUserNotFound(err)) return resolve(resetChallenge(user, email, null));
        reject(asError(err));
      },
    });
  });
}

function confirmPasswordReset(
  user: CognitoUser,
  email: string,
  code: string,
  newPassword: string,
): Promise<Session> {
  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: () => {
        // `ConfirmForgotPassword` returns no tokens, so the password it just
        // set is spent on a normal SRP sign-in. Doing it here rather than
        // returning the person to an empty sign-in form: they have typed the
        // password twice already, and a reset that ends at the screen it
        // started on reads as a failure.
        signIn(email, newPassword).then((result) => {
          if (result.kind === "session") return resolve(result.session);
          // Unreachable: `ForgotPassword` refuses an account that still owes
          // a first password. Better a sentence than a hung promise.
          reject(new Error("The password was changed. Sign in with it to continue."));
        }, (err: unknown) => reject(asError(err)));
      },
      onFailure: (err: unknown) => reject(asError(err)),
    });
  });
}

/** Seconds until the access token expires, from its `exp` claim. */
export function secondsToExpiry(accessToken: string): number {
  try {
    const payload = JSON.parse(atob(accessToken.split(".")[1]!.replace(/-/g, "+").replace(/_/g, "/"))) as { exp?: number };
    return payload.exp ? payload.exp - Math.floor(Date.now() / 1000) : 0;
  } catch {
    return 0;
  }
}

/**
 * Force a refresh with the refresh token, ahead of expiry. `getSession`
 * only refreshes once the access token is already invalid, which leaves a
 * window where a request fails; a tablet open all service must not hit it.
 */
export function refreshSession(): Promise<Session | null> {
  return new Promise((resolve) => {
    const user = pool.getCurrentUser();
    if (!user) return resolve(null);
    user.getSession((err: Error | null, s: CognitoUserSession | null) => {
      if (err || !s) return resolve(null);
      user.refreshSession(s.getRefreshToken(), (err2: Error | null, fresh: CognitoUserSession | null) => {
        if (err2 || !fresh) return resolve(null);
        resolve(sessionFrom(user, fresh));
      });
    });
  });
}

export function signOut(): void {
  pool.getCurrentUser()?.signOut();
  localStorage.removeItem(EMAIL_KEY);
}

// ------------------------------------------------- setting up a second step
//
// Done against Cognito with the person's own access token: the API never
// sees the authenticator secret or a code. Afterwards the screen asks the
// API to look again (`POST /v1/me/mfa/sync`), which is what the
// organisation's rule is enforced on.

/** The signed-in user, with their session loaded so its token can be used. */
function signedInUser(): Promise<{ user: CognitoUser; session: CognitoUserSession }> {
  return new Promise((resolve, reject) => {
    const user = pool.getCurrentUser();
    if (!user) return reject(new Error("Sign in again to change two-step sign-in."));
    user.getSession((err: Error | null, s: CognitoUserSession | null) => {
      if (err || !s || !s.isValid()) return reject(new Error("Sign in again to change two-step sign-in."));
      resolve({ user, session: s });
    });
  });
}

/** An authenticator app's secret, and the link an app reads from a QR code. */
export interface AuthenticatorSetup {
  readonly secret: string;
  readonly uri: string;
}

/** Step one of an authenticator app: Cognito makes a secret for this person. */
export async function startAuthenticator(): Promise<AuthenticatorSetup> {
  const { user } = await signedInUser();
  const secret = await new Promise<string>((resolve, reject) => {
    user.associateSoftwareToken({
      associateSecretCode: resolve,
      onFailure: (err: unknown) => reject(asError(err)),
    });
  });
  const who = localStorage.getItem(EMAIL_KEY) ?? user.getUsername();
  const label = encodeURIComponent(`Bar OS:${who}`);
  return { secret, uri: `otpauth://totp/${label}?secret=${secret}&issuer=${encodeURIComponent("Bar OS")}` };
}

/** Step two: the first code from the app proves it is set up; then it is turned on. */
export async function confirmAuthenticator(code: string): Promise<void> {
  const { user } = await signedInUser();
  await new Promise<void>((resolve, reject) => {
    user.verifySoftwareToken(code, "Bar OS", {
      onSuccess: () => resolve(),
      onFailure: (err: unknown) => reject(asError(err)),
    });
  });
  await chooseSecondStep("totp");
}

/** Turn the authenticator app on as the second step, or turn it off. */
export async function chooseSecondStep(method: MfaMethod | null): Promise<void> {
  const { user } = await signedInUser();
  await new Promise<void>((resolve, reject) => {
    user.setUserMfaPreference(
      null,
      { Enabled: method === "totp", PreferredMfa: method === "totp" },
      (err) => (err ? reject(asError(err)) : resolve()),
    );
  });
}
